import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const PlaisanceDuTouch = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Plaisance-du-Touch – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert réparation fenêtres à Plaisance-du-Touch (31830). Intervention sous 2h pour fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Nathalie Durand',
      location: 'Quartier Padoue',
      text: 'Fenêtre bloquée depuis plusieurs jours. Intervention rapide et efficace. Le technicien a été très professionnel.',
      rating: 5
    },
    {
      name: 'Philippe Garnier',
      location: 'Centre-ville Plaisance',
      text: 'Remplacement de double vitrage. Travail impeccable et prix raisonnable. Je recommande vivement !',
      rating: 5
    },
    {
      name: 'Céline Blanc',
      location: 'Quartier Ramiers',
      text: 'Problème d\'isolation résolu rapidement. Équipe compétente et à l\'écoute. Très satisfaite du service !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-water-flash-line', title: 'Condensation', desc: 'Humidité et double vitrage' },
    { icon: 'ri-wind-line', title: 'Isolation thermique', desc: 'Joints usés et courants d\'air' },
    { icon: 'ri-lock-unlock-line', title: 'Mécanismes grippés', desc: 'Usure et entretien' },
    { icon: 'ri-contrast-drop-line', title: 'Vitrage endommagé', desc: 'Bris de glace et fissures' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Plaisance-du-Touch - Haute-Garonne (31830)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Plaisance-du-Touch</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Intervention rapide <strong>sous 2h</strong> pour tous vos problèmes de fenêtres
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Plaisance</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 9 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Plaisance-du-Touch</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">Plaisance-du-Touch</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous connaissons les spécificités de votre commune
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à Plaisance-du-Touch ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>9 km à l'ouest de Toulouse</strong>, Plaisance-du-Touch est une commune résidentielle en pleine expansion. Les quartiers <strong>Padoue, Centre-ville et Ramiers</strong> connaissent des problèmes typiques liés au climat toulousain : condensation, usure des joints et mécanismes grippés. La proximité du Touch et l'humidité ambiante accentuent ces phénomènes.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Plaisance-du-Touch</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Fenêtres Mixtes Bois-Aluminium */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/fe2c584c63c5b63b4ddee4a525d12e87.jpg"
              alt="Fenêtres mixtes bois-aluminium Plaisance-du-Touch"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-contrast-2-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Mixte Bois-Alu</p>
                  <p className="text-sm text-gray-600">Haut de gamme</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-teal-600">Fenêtres Mixtes Bois-Aluminium</strong> à Plaisance-du-Touch
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Les <strong>fenêtres mixtes bois-aluminium</strong> représentent le summum de la menuiserie haut de gamme à Plaisance-du-Touch. Cette solution combine la <strong>chaleur et l'élégance du bois</strong> à l'intérieur avec la <strong>robustesse et la modernité de l'aluminium</strong> à l'extérieur, offrant le meilleur des deux matériaux sans leurs inconvénients.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous proposons l'<strong>installation de fenêtres bois-aluminium à Plaisance-du-Touch</strong> avec <strong>bois noble à l'intérieur</strong> (chêne, méranti) pour un cachet authentique, <strong>habillage aluminium extérieur</strong> sans entretien et résistant aux intempéries, <strong>isolation thermique exceptionnelle</strong> et <strong>large choix de coloris</strong> pour l'aluminium extérieur. Solution idéale pour maisons d'architecte et rénovations haut de gamme.
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-home-heart-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Intérieur Bois Noble</h4>
                <p className="text-gray-600 text-sm">Chaleur et authenticité du bois à l'intérieur pour un confort visuel optimal</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-shield-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Extérieur Aluminium</h4>
                <p className="text-gray-600 text-sm">Protection maximale contre intempéries, zéro entretien, durabilité 50+ ans</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-temp-cold-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Isolation Thermique Optimale</h4>
                <p className="text-gray-600 text-sm">Double isolation naturelle du bois + rupture de pont thermique aluminium</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-palette-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Personnalisation Totale</h4>
                <p className="text-gray-600 text-sm">Essence de bois intérieur + coloris RAL extérieur au choix</p>
              </div>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Bois-Aluminium
          </a>
        </motion.div>
      </div>

      {/* NOUVEAU BLOC 2 - Traitement Anti-Humidité */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="order-2 lg:order-1"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-orange-600">Solutions Anti-Humidité</strong> pour Fenêtres à Plaisance-du-Touch
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Plaisance-du-Touch connaît des problèmes d'<strong>humidité et de condensation</strong> sur les fenêtres, particulièrement en hiver. Notre expertise en <strong>traitement anti-humidité des fenêtres à Plaisance-du-Touch</strong> vous permet de résoudre définitivement ces désagréments : condensation, moisissures, joints noircis et dégradation des menuiseries.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous proposons des solutions complètes : <strong>remplacement de joints d'étanchéité</strong>, <strong>installation de double vitrage anti-condensation</strong> avec gaz argon, <strong>pose de grilles d'aération hygroréglables</strong>, <strong>traitement hydrofuge des menuiseries</strong> et <strong>amélioration de la ventilation</strong>. Nos interventions garantissent l'élimination durable de l'humidité à Plaisance-du-Touch.
          </p>

          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-drop-line text-blue-600 mr-2 text-xl"></i>
              Problèmes Résolus
            </h4>
            <div className="space-y-3">
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Condensation sur vitres</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Moisissures joints</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Bois gonflé/pourri</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Odeurs désagréables</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-contrast-drop-2-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Vitrage Anti-Condensation</h4>
                <p className="text-gray-600 text-sm">Double vitrage avec intercalaire warm edge évitant les ponts thermiques</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-wind-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Ventilation Hygroréglable</h4>
                <p className="text-gray-600 text-sm">Grilles d'aération qui s'adaptent automatiquement au taux d'humidité</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-shield-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Traitement Préventif</h4>
                <p className="text-gray-600 text-sm">Protection hydrofuge et fongicide longue durée des menuiseries</p>
              </div>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Diagnostic Humidité Gratuit
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=window%20moisture%20problem%20showing%20water%20droplets%20condensation%20on%20glass%20mold%20on%20frame%20before%20and%20after%20comparison%20anti%20humidity%20treatment%20solution%20professional%20repair%20demonstrating%20dry%20healthy%20window%20in%20home%20simple%20clean%20background&width=800&height=600&seq=plaisance-humidity-solution-v1&orientation=landscape"
              alt="Traitement anti-humidité fenêtres Plaisance-du-Touch"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-drop-line text-blue-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Anti-Humidité</p>
                  <p className="text-sm text-gray-600">Solution durable</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Plaisance-du-Touch</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Padoue', 'Ramiers', 'Bascule', 'Fontaine', 'Garonne', 'Mirail', 'Lardenne'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Plaisance-du-Touch ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default PlaisanceDuTouch;